import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Database, RefreshCw, Search, MapPin, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

// Clean-slate Parcel Data Management. Every county is its own MongoDB
// collection (parcels_v2_<county_id>). Manual upload only — no auto-fetch.
const AdminParcelDataV2 = () => {
  const [data, setData] = useState({ counties: {}, _total: { total_counties: 0, counties_with_data: 0, total_parcels: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('ALL');
  // Global coord-audit rollup — surfaces any county with bad-coord rows.
  const [globalAudit, setGlobalAudit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [res, auditRes] = await Promise.all([
      safeJsonFetch(`${API}/api/admin/parcel-v2/counties`, { headers: authHeaders() }),
      safeJsonFetch(`${API}/api/admin/parcel-v2/coord-audit-all`, { headers: authHeaders() }),
    ]);
    if (res.ok && res.data) {
      setData(res.data);
      setError(null);
    } else {
      setError(res.error || `HTTP ${res.status}`);
    }
    setGlobalAudit(auditRes.ok ? auditRes.data : null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counties = Object.values(data.counties || {});
  const states = Array.from(new Set(counties.map((c) => c.state))).sort();
  const filtered = counties.filter((c) => {
    if (stateFilter !== 'ALL' && c.state !== stateFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return c.name.toLowerCase().includes(q) || c.county_id.toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-parcel-v2-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="parcel-v2-title">Parcel Data Mgmt</h1>
          <p className="text-sm text-gray-500 mt-1">
            Clean-slate per-county parcel system. Manual uploads only — each county has its own MongoDB collection.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="parcel-v2-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white" data-testid="kpi-total-counties">
          <p className="text-blue-100 text-xs uppercase tracking-wide">Counties Configured</p>
          <p className="text-3xl font-bold mt-1">{data._total?.total_counties || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white" data-testid="kpi-counties-with-data">
          <p className="text-emerald-100 text-xs uppercase tracking-wide">Counties With Data</p>
          <p className="text-3xl font-bold mt-1">{data._total?.counties_with_data || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white" data-testid="kpi-total-parcels">
          <p className="text-purple-100 text-xs uppercase tracking-wide">Total Parcels</p>
          <p className="text-3xl font-bold mt-1">{(data._total?.total_parcels || 0).toLocaleString()}</p>
        </div>
        {/* Coord health tile — emerald when zero bad rows, amber when any.
            Single-glance status for the whole parcel-data layer. */}
        <div
          className={`rounded-xl p-5 text-white ${
            (globalAudit?.global_bad_coord_parcels || 0) > 0
              ? 'bg-gradient-to-br from-amber-500 to-amber-600'
              : 'bg-gradient-to-br from-teal-500 to-teal-600'
          }`}
          data-testid="kpi-coord-health"
        >
          <p className={`${(globalAudit?.global_bad_coord_parcels || 0) > 0 ? 'text-amber-100' : 'text-teal-100'} text-xs uppercase tracking-wide flex items-center gap-1`}>
            {(globalAudit?.global_bad_coord_parcels || 0) > 0 ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
            Bad Coord Parcels
          </p>
          <p className="text-3xl font-bold mt-1">
            {(globalAudit?.global_bad_coord_parcels || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Per-county breakdown of bad coords — shown only when there's any.
          Each entry deep-links into that county's page where the existing
          Coordinate Audit card offers a one-click cleanup button. */}
      {globalAudit && globalAudit.global_bad_coord_parcels > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="parcel-v2-bad-coord-counties">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" /> Counties with bad-coord parcels — open each one to clean up
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {globalAudit.counties
              .filter((c) => c.bad_coord_parcels > 0)
              .map((c) => (
                <Link
                  key={c.county_id}
                  to={`/admin/parcel-data-v2/county/${c.county_id}`}
                  className="flex items-center justify-between bg-white border border-amber-200 rounded-md px-3 py-2 hover:bg-amber-100 hover:border-amber-400 transition"
                  data-testid={`bad-coord-county-${c.county_id}`}
                >
                  <span className="text-sm font-medium text-gray-900 truncate">{c.county_name}</span>
                  <Badge className="bg-amber-200 text-amber-900 border border-amber-400 ml-2 shrink-0">
                    {c.bad_coord_parcels.toLocaleString()} bad
                  </Badge>
                </Link>
              ))}
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm" data-testid="parcel-v2-error">
          <p className="font-semibold text-rose-900 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Failed to load V2 counties
          </p>
          <p className="text-rose-700 mt-1">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search county name, id, or city..."
            className="pl-9"
            data-testid="parcel-v2-search"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setStateFilter('ALL')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${stateFilter === 'ALL' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            data-testid="parcel-v2-state-ALL"
          >All States</button>
          {states.map((s) => (
            <button
              key={s}
              onClick={() => setStateFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${stateFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              data-testid={`parcel-v2-state-${s}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {/* County cards */}
      {loading && !counties.length ? (
        <div className="text-center py-12 text-gray-400" data-testid="parcel-v2-loading">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="parcel-v2-empty-filter">
          No counties match the current filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const hasData = c.parcel_count > 0;
            const covPct = Math.min(c.coverage_percent || 0, 100);
            const covColor =
              covPct >= 90 ? 'bg-emerald-500' :
              covPct >= 50 ? 'bg-blue-500' :
              covPct > 0 ? 'bg-amber-500' : 'bg-gray-300';
            return (
              <Link
                key={c.county_id}
                to={`/admin/parcel-data-v2/county/${c.county_id}`}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition group"
                data-testid={`parcel-v2-county-card-${c.county_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">{c.state}{c.city ? ` · ${c.city}` : ''}</p>
                    <h3 className="font-bold text-gray-900 mt-0.5 group-hover:text-blue-700 transition truncate">{c.name}</h3>
                    <code className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded mt-1 inline-block">{c.county_id}</code>
                  </div>
                  {hasData ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Loaded
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border border-gray-200 shrink-0">Empty</Badge>
                  )}
                </div>

                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{(c.parcel_count || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">of {(c.expected_total || 0).toLocaleString()} expected</p>
                  </div>
                  <div className="text-right">
                    {c.has_2dsphere ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">2dsphere ✓</Badge>
                    ) : (
                      <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">No index</Badge>
                    )}
                  </div>
                </div>

                <div className="mt-3 w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-testid={`parcel-v2-coverage-${c.county_id}`}>
                  <div className={`h-full transition-all duration-700 ${covColor}`} style={{ width: `${covPct}%` }} />
                </div>

                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className={`font-semibold ${covPct >= 50 ? 'text-emerald-700' : covPct > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{covPct.toFixed(1)}%</span>
                  {c.last_upload?.at && (
                    <span className="text-gray-400">
                      <MapPin className="w-3 h-3 inline mr-0.5" />
                      {new Date(c.last_upload.at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Legacy link */}
      <div className="border-t border-gray-100 pt-6 text-center text-sm text-gray-400">
        Looking for the legacy system?{' '}
        <Link to="/admin/legacy-parcel-data" className="text-blue-600 hover:underline" data-testid="legacy-parcel-data-link">
          Legacy Parcel Data
        </Link>
      </div>
    </div>
  );
};

export default AdminParcelDataV2;
